import React from "react";
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";
import { joinClasses } from "./shared.js";

const ARROW_SIZE = 10;
const DEFAULT_DELAY_MS = 120;
const DEFAULT_FREEZE_AFTER_MS = 0;
const DEFAULT_INTERACTIVE_CLOSE_DELAY_MS = 180;
const DEFAULT_MAX_WIDTH = 320;
const DEFAULT_ACCENT_COLOR = "var(--route-loading)";

const arrowBorderByPlacement = {
  top: {
    borderRight: "0.5px solid rgba(0,0,0,0.08)",
    borderBottom: "0.5px solid rgba(0,0,0,0.08)",
  },
  bottom: {
    borderTop: "0.5px solid rgba(0,0,0,0.08)",
    borderLeft: "0.5px solid rgba(0,0,0,0.08)",
  },
  left: {
    borderTop: "0.5px solid rgba(0,0,0,0.08)",
    borderRight: "0.5px solid rgba(0,0,0,0.08)",
  },
  right: {
    borderBottom: "0.5px solid rgba(0,0,0,0.08)",
    borderLeft: "0.5px solid rgba(0,0,0,0.08)",
  },
};

const staticSideByPlacement = {
  top: "bottom",
  right: "left",
  bottom: "top",
  left: "right",
};

const interactiveBridgeStyleByPlacement = {
  top: { bottom: -12, height: 14, left: -8, right: -8 },
  right: { bottom: -8, left: -12, top: -8, width: 14 },
  bottom: { height: 14, left: -8, right: -8, top: -12 },
  left: { bottom: -8, right: -12, top: -8, width: 14 },
};

function targetIsInside(element, target) {
  if (!element || typeof Node === "undefined" || !(target instanceof Node)) {
    return false;
  }

  return element.contains(target);
}

function assignRef(ref, value) {
  if (typeof ref === "function") {
    ref(value);
    return;
  }

  if (ref && typeof ref === "object") {
    ref.current = value;
  }
}

function mergeRefs(...refs) {
  return (value) => {
    refs.forEach((ref) => {
      assignRef(ref, value);
    });
  };
}

function composeEventHandlers(userHandler, internalHandler) {
  return (event) => {
    userHandler?.(event);
    internalHandler?.(event);
  };
}

export function HoverCard({
  content,
  title,
  titleTrailing = null,
  placement = "top",
  delay = DEFAULT_DELAY_MS,
  freezeAfterMs = DEFAULT_FREEZE_AFTER_MS,
  interactive = false,
  interactiveCloseDelay = DEFAULT_INTERACTIVE_CLOSE_DELAY_MS,
  accentColor = DEFAULT_ACCENT_COLOR,
  closeOnReferenceClick = false,
  closeOnScroll = false,
  openOnClick = false,
  openOnTouch = false,
  hoverOnly = false,
  disabled = false,
  maxWidth = DEFAULT_MAX_WIDTH,
  width,
  referenceAsChild = false,
  compactTitle = true,
  tight = true,
  flat = true,
  // Au-dessus des panneaux fixes (z-[10000]) qui couvrent les infobulles ordinaires.
  elevated = false,
  // « end » : sans place à gauche ni à droite, la bulle passe dessous au lieu de couvrir son déclencheur.
  fallbackAxisSideDirection = "none",
  children,
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isPinned, setIsPinned] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const [freezeProgress, setFreezeProgress] = React.useState(0);
  const openTimerRef = React.useRef(null);
  const closeTimerRef = React.useRef(null);
  const pinTimerRef = React.useRef(null);
  const animationFrameRef = React.useRef(null);
  const progressFrameRef = React.useRef(null);
  const arrowRef = React.useRef(null);
  const hoverCardId = React.useId();
  const touchReferenceRef = React.useRef(false);

  const {
    refs,
    floatingStyles,
    isPositioned,
    middlewareData,
    placement: resolvedPlacement,
  } = useFloating({
    open: isOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(ARROW_SIZE / 2 + 6),
      flip({ padding: 12, fallbackAxisSideDirection }),
      shift({ padding: 12, crossAxis: flat }),
      arrow({ element: arrowRef, padding: 10 }),
    ],
  });

  const clearOpenTimer = React.useCallback(() => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const clearCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const clearPinTimer = React.useCallback(() => {
    if (pinTimerRef.current) {
      window.clearTimeout(pinTimerRef.current);
      pinTimerRef.current = null;
    }
  }, []);

  const clearProgressFrame = React.useCallback(() => {
    if (progressFrameRef.current) {
      window.cancelAnimationFrame(progressFrameRef.current);
      progressFrameRef.current = null;
    }
  }, []);

  const closeHoverCard = React.useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    clearPinTimer();
    clearProgressFrame();

    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    setFreezeProgress(0);
    setIsPinned(false);
    setIsVisible(false);
    setIsOpen(false);
  }, [clearCloseTimer, clearOpenTimer, clearPinTimer, clearProgressFrame]);

  React.useEffect(() => {
    if (!hoverOnly) return undefined;

    // Switching tabs can leave the pointer state stale without a mouseleave.
    const handleVisibilityChange = () => {
      if (document.hidden) closeHoverCard();
    };
    window.addEventListener("blur", closeHoverCard);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("blur", closeHoverCard);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [closeHoverCard, hoverOnly]);

  const openHoverCard = React.useCallback(() => {
    if (disabled || content == null) {
      return;
    }

    clearOpenTimer();
    clearCloseTimer();
    setIsVisible(false);
    setIsOpen(true);
  }, [clearCloseTimer, clearOpenTimer, content, disabled]);

  React.useEffect(() => {
    if (!closeOnScroll) return undefined;
    document.addEventListener("scroll", closeHoverCard, true);
    return () => document.removeEventListener("scroll", closeHoverCard, true);
  }, [closeHoverCard, closeOnScroll]);

  const scheduleOpen = React.useCallback(() => {
    if (disabled || content == null || isOpen) {
      return;
    }

    clearOpenTimer();
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null;
      openHoverCard();
    }, delay);
  }, [clearOpenTimer, content, delay, disabled, isOpen, openHoverCard]);

  const scheduleInteractiveClose = React.useCallback(() => {
    clearCloseTimer();
    const normalizedDelay = Number.isFinite(interactiveCloseDelay)
      ? Math.max(interactiveCloseDelay, 0)
      : DEFAULT_INTERACTIVE_CLOSE_DELAY_MS;
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      closeHoverCard();
    }, normalizedDelay);
  }, [clearCloseTimer, closeHoverCard, interactiveCloseDelay]);

  React.useEffect(() => {
    if (!isOpen || !isPositioned) {
      setIsVisible(false);
      return undefined;
    }

    animationFrameRef.current = window.requestAnimationFrame(() => {
      setIsVisible(true);
      animationFrameRef.current = null;
    });

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isOpen, isPositioned]);

  React.useEffect(() => {
    if (!disabled) {
      return undefined;
    }

    closeHoverCard();
    return undefined;
  }, [closeHoverCard, disabled]);

  React.useEffect(() => {
    return () => {
      clearOpenTimer();
      clearCloseTimer();
      clearPinTimer();
      clearProgressFrame();

      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [clearCloseTimer, clearOpenTimer, clearPinTimer, clearProgressFrame]);

  React.useEffect(() => {
    clearPinTimer();
    clearProgressFrame();

    if (!isOpen || disabled || content == null || !Number.isFinite(freezeAfterMs) || freezeAfterMs <= 0) {
      if (!isPinned) {
        setFreezeProgress(0);
      }
      return undefined;
    }

    if (isPinned) {
      setFreezeProgress(1);
      return undefined;
    }

    setFreezeProgress(0);
    const startedAt = window.performance.now();

    const updateProgress = (now) => {
      const elapsedMs = Math.max(now - startedAt, 0);
      const nextProgress = Math.min(elapsedMs / freezeAfterMs, 1);
      setFreezeProgress(nextProgress);

      if (nextProgress < 1) {
        progressFrameRef.current = window.requestAnimationFrame(updateProgress);
      } else {
        progressFrameRef.current = null;
      }
    };

    progressFrameRef.current = window.requestAnimationFrame(updateProgress);
    pinTimerRef.current = window.setTimeout(() => {
      pinTimerRef.current = null;
      clearProgressFrame();
      setFreezeProgress(1);
      setIsPinned(true);
      setIsOpen(true);
    }, freezeAfterMs);

    return () => {
      clearPinTimer();
      clearProgressFrame();
    };
  }, [clearPinTimer, clearProgressFrame, content, disabled, freezeAfterMs, isOpen, isPinned]);

  React.useEffect(() => {
    if ((!isPinned && !(openOnClick && isOpen)) || typeof document === "undefined") {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (
        targetIsInside(refs.reference.current, event.target)
        || targetIsInside(refs.floating.current, event.target)
      ) {
        return;
      }

      closeHoverCard();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeHoverCard();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [closeHoverCard, isPinned, isOpen, openOnClick, refs.floating, refs.reference]);

  const handleReferenceMouseEnter = () => {
    if (openOnTouch && touchReferenceRef.current) return;
    clearCloseTimer();
    scheduleOpen();
  };

  const handleReferenceFocus = () => {
    if (hoverOnly) return;
    if (openOnTouch && touchReferenceRef.current) return;
    scheduleOpen();
  };

  const handleReferenceMouseLeave = (event) => {
    if (targetIsInside(refs.floating.current, event.relatedTarget)) {
      return;
    }

    if (!isPinned) {
      clearPinTimer();
    }

    if (isPinned) {
      return;
    }

    if (interactive && isOpen) {
      scheduleInteractiveClose();
      return;
    }

    closeHoverCard();
  };

  const handleReferenceBlur = (event) => {
    if (
      targetIsInside(refs.reference.current, event.relatedTarget)
      || targetIsInside(refs.floating.current, event.relatedTarget)
    ) {
      return;
    }

    if (!isPinned) {
      clearPinTimer();
    }

    if (isPinned) {
      return;
    }

    closeHoverCard();
  };

  const handleReferencePointerDown = (event) => {
    touchReferenceRef.current = event.pointerType === "touch";
    if (openOnTouch && touchReferenceRef.current) clearOpenTimer();
    if (!closeOnReferenceClick) {
      return;
    }

    closeHoverCard();
  };

  const handleReferenceClick = (event) => {
    if (openOnTouch && touchReferenceRef.current) {
      const action = event.target.closest?.("button, a, input, select, textarea");
      if (action && action !== refs.reference.current) return;
      if (isOpen) closeHoverCard();
      else { openHoverCard(); setIsPinned(true); }
      return;
    }
    if (!openOnClick) return;
    if (isOpen) closeHoverCard();
    else openHoverCard();
  };

  const handleFloatingMouseLeave = (event) => {
    if (targetIsInside(refs.reference.current, event.relatedTarget)) {
      return;
    }

    if (!isPinned) {
      clearPinTimer();
    }

    if (isPinned) {
      return;
    }

    closeHoverCard();
  };

  const basePlacement = (resolvedPlacement || "top").split("-")[0];
  const arrowData = middlewareData?.arrow || {};
  const showFreezeOutline = Number.isFinite(freezeAfterMs) && freezeAfterMs > 0;
  const freezeOutlineProgress = isPinned ? 1 : Math.min(Math.max(freezeProgress, 0), 1);
  const arrowStyle = {
    left: arrowData.x != null ? `${arrowData.x}px` : "",
    top: arrowData.y != null ? `${arrowData.y}px` : "",
    right: "",
    bottom: "",
    [staticSideByPlacement[basePlacement]]: `-${ARROW_SIZE / 2}px`,
    ...(arrowBorderByPlacement[basePlacement] || arrowBorderByPlacement.top),
  };
  const freezeOutlineStyle = showFreezeOutline
    ? {
        background: freezeOutlineProgress >= 1
          ? accentColor
          : `conic-gradient(from -90deg, ${accentColor} 0turn ${freezeOutlineProgress}turn, transparent ${freezeOutlineProgress}turn 1turn)`,
        padding: "1.5px",
        WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
        WebkitMaskComposite: "xor",
        maskComposite: "exclude",
        opacity: isVisible ? 1 : 0,
      }
    : null;

  const hoverCardNode =
    isOpen && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={refs.setFloating}
            id={hoverCardId}
            data-grain-floating-surface="hovercard"
            className={joinClasses([
              "relative border-[0.5px] border-[rgba(0,0,0,0.08)] bg-white transition-opacity duration-100 ease-out",
              elevated ? "z-[10001]" : "z-9998",
              flat
                ? "rounded-[4px] shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
                : "rounded-[14px] shadow-[0_16px_40px_rgba(0,0,0,0.13),0_4px_12px_rgba(0,0,0,0.07),0_1px_3px_rgba(0,0,0,0.04)]",
              compactTitle ? (tight ? "px-2.5 py-1.5" : "px-3 py-2") : "px-4 py-3.5",
              isVisible ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
            ])}
            style={{
              ...floatingStyles,
              maxWidth: `min(${typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth}, calc(100vw - 24px))`,
              ...(width != null ? { width } : null),
            }}
            onMouseEnter={() => {
              clearOpenTimer();
              clearCloseTimer();
            }}
            onMouseLeave={handleFloatingMouseLeave}
          >
            {interactive ? (
              <div
                aria-hidden="true"
                className="absolute z-0"
                data-hover-card-interaction-bridge="true"
                style={interactiveBridgeStyleByPlacement[basePlacement] || interactiveBridgeStyleByPlacement.top}
              />
            ) : null}
            {freezeOutlineStyle ? (
              <div
                aria-hidden="true"
                className={joinClasses(["pointer-events-none absolute inset-0 transition-opacity duration-150 ease-out", flat ? "rounded-[4px]" : "rounded-[14px]"])}
                style={freezeOutlineStyle}
              />
            ) : null}
            <div
              ref={arrowRef}
              aria-hidden="true"
              style={{
                position: "absolute",
                width: ARROW_SIZE,
                height: ARROW_SIZE,
                background: "white",
                transform: "rotate(45deg)",
                ...arrowStyle,
              }}
            />
            <div className="relative z-1">
              {title || titleTrailing ? (
                <div
                  className={joinClasses([
                    "flex items-center justify-between gap-2 border-b-[0.5px] border-[rgba(0,0,0,0.07)]",
                    compactTitle ? (tight ? "mb-0.5 pb-0.5" : "mb-1 pb-1") : "mb-2.5 pb-2.5",
                  ])}
                >
                  {title ? (
                    <div className="min-w-0 break-words text-pretty text-[10px] font-black uppercase tracking-[0.08em] text-floating-faint">
                      {title}
                    </div>
                  ) : <span aria-hidden="true" />}
                  {titleTrailing ? (
                    <div className="shrink-0">{titleTrailing}</div>
                  ) : null}
                </div>
              ) : null}
              <div className="text-[12px] leading-[1.55] text-floating-copy">{content}</div>
            </div>
          </div>,
          getReactPortalRoot(),
        )
      : null;

  const canUseChildAsReference = referenceAsChild && React.isValidElement(children) && typeof children.type === "string";
  const referenceProps = {
    "aria-describedby": isOpen ? hoverCardId : undefined,
    onBlur: handleReferenceBlur,
    onFocus: handleReferenceFocus,
    onMouseEnter: handleReferenceMouseEnter,
    onMouseLeave: handleReferenceMouseLeave,
    onPointerDown: handleReferencePointerDown,
    onClick: handleReferenceClick,
  };

  const referenceNode = canUseChildAsReference
    ? React.cloneElement(children, {
        ...referenceProps,
        ref: mergeRefs(children.ref, refs.setReference),
        onBlur: composeEventHandlers(children.props.onBlur, handleReferenceBlur),
        onFocus: composeEventHandlers(children.props.onFocus, handleReferenceFocus),
        onMouseEnter: composeEventHandlers(children.props.onMouseEnter, handleReferenceMouseEnter),
        onMouseLeave: composeEventHandlers(children.props.onMouseLeave, handleReferenceMouseLeave),
        onPointerDown: composeEventHandlers(children.props.onPointerDown, handleReferencePointerDown),
        onClick: composeEventHandlers(children.props.onClick, handleReferenceClick),
      })
    : (
      <div
        ref={refs.setReference}
        className="block min-w-0"
        tabIndex={openOnTouch ? 0 : undefined}
        aria-describedby={isOpen ? hoverCardId : undefined}
        onMouseEnter={handleReferenceMouseEnter}
        onMouseLeave={handleReferenceMouseLeave}
        onFocus={handleReferenceFocus}
        onBlur={handleReferenceBlur}
        onPointerDown={handleReferencePointerDown}
        onClick={handleReferenceClick}
      >
        {children}
      </div>
    );

  return (
    <>
      {referenceNode}
      {hoverCardNode}
    </>
  );
}
